import React, { useState, useEffect } from 'react';
import {
  Button,
  Toastify,
  SingleSelect,
  Label,
  SpinnerLoader,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from 'lift-ui';
import { Plus, RefreshCw } from 'lucide-react';
import apiClient from '@lift-admin/utils/apiClient';
import { useReportConfigurations } from './hooks';
import ConfigurationTable from './components/ConfigurationTable';
import ConfigurationForm from './components/ConfigurationForm';
import type { Brand, ConfigurationFilterStatus } from './types';

const ReportConfiguration: React.FC = () => {
  const [brands, setBrands] = useState<Brand[]>([]);
  const [selectedBrand, setSelectedBrand] = useState<Brand | null>(null);
  const [isLoadingBrands, setIsLoadingBrands] = useState(true);
  const [isAddSheetOpen, setIsAddSheetOpen] = useState(false);
  const [hasFiscalCalendar, setHasFiscalCalendar] = useState(false);
  const [isCheckingFiscal, setIsCheckingFiscal] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [statusFilter, setStatusFilter] = useState<ConfigurationFilterStatus>('all');

  const {
    data: configurations,
    isLoading: isLoadingConfigs,
    refetch: refetchConfigs,
  } = useReportConfigurations(selectedBrand?.id, statusFilter);

  useEffect(() => {
    const fetchBrands = async () => {
      try {
        const response = await apiClient.get('/brand/v1/admin/brands/all');
        const brandList = response.data?.response || [];
        setBrands(brandList);
      } catch (error) {
        console.error('Error fetching brands:', error);
        Toastify('error', 'Failed to fetch brands');
      } finally {
        setIsLoadingBrands(false);
      }
    };

    fetchBrands();
  }, []);

  const handleBrandChange = async (brandIdStr: string) => {
    const brandId = parseInt(brandIdStr);
    const brand = brands.find((b) => b.id === brandId);
    setSelectedBrand(brand || null);
    setStatusFilter('all'); // Reset filter when brand changes
    
    // Check if brand has fiscal calendar
    if (brand) {
      setIsCheckingFiscal(true);
      try {
        const response = await apiClient.get(`/fiscal/v1/fiscal_calendar/brand/${brandId}`);
        const fiscalData = response.data?.response || [];
        setHasFiscalCalendar(fiscalData.length > 0);
      } catch (error) {
        console.error('Error checking fiscal calendar:', error);
        setHasFiscalCalendar(false);
      } finally {
        setIsCheckingFiscal(false);
      }
    } else {
      setHasFiscalCalendar(false);
    }
  };

  const handleAddSuccess = () => {
    setIsAddSheetOpen(false);
    refetchConfigs();
    // Toast is already shown in ConfigurationForm, no need for duplicate
  };

  const handleRefresh = async (showToast: boolean = true) => {
    try {
      await refetchConfigs();
      setRefreshKey(prev => prev + 1); // Force table to recalculate time
      if (showToast) {
        Toastify('success', 'Configurations refreshed successfully');
      }
    } catch (error) {
      Toastify('error', 'Failed to refresh configurations');
    }
  };

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Page Header */}
      <div className="flex items-center justify-between px-8 py-4 border-b bg-white">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Report Configuration</h1>
          <p className="text-sm text-gray-500 mt-1">
            Configure Breakdowns and Reach & Frequency data fetches
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleRefresh()}
            disabled={!selectedBrand || isLoadingConfigs}
            className="flex items-center gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${isLoadingConfigs ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            size="sm"
            onClick={() => setIsAddSheetOpen(true)}
            disabled={!selectedBrand}
            className="flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Add Configuration
          </Button>
        </div>
      </div>

      {/* Brand Selection */}
      <div className="px-8 py-4 border-b bg-gray-50">
        <div className="flex gap-4 items-end">
          <div className="flex-1 max-w-md">
            <Label className="block mb-2">Select Brand</Label>
            {isLoadingBrands ? (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <SpinnerLoader className="h-4 w-4" />
                Loading brands...
              </div>
            ) : (
              <SingleSelect
                options={brands.map((b) => ({ label: b.name, value: b.id.toString() }))}
                defaultValue={selectedBrand?.id.toString()}
                onChange={handleBrandChange}
                placeholder="Select a brand to view configurations..."
                containerClassName="w-full"
              />
            )}
          </div>
          
          {selectedBrand && (
            <div className="flex-1 max-w-xs">
              <Label className="block mb-2">Filter by Status</Label>
              <SingleSelect
                options={[
                  { label: 'Show All', value: 'all' },
                  { label: 'Active Only', value: 'active' },
                  { label: 'Scheduled for Deactivation', value: 'scheduled' },
                  { label: 'Deactivated Only', value: 'deactivated' },
                ]}
                defaultValue={statusFilter}
                onChange={(value) => setStatusFilter(value as ConfigurationFilterStatus)}
                placeholder="Filter by status"
                containerClassName="w-full"
              />
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-0 px-8 pt-6 pb-2">
        {!selectedBrand ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <p className="text-gray-500 text-lg">
                Select a brand to view and manage report configurations
              </p>
            </div>
          </div>
        ) : isLoadingConfigs ? (
          <div className="flex-1 flex items-center justify-center">
            <SpinnerLoader className="h-8 w-8" />
          </div>
        ) : (
          <ConfigurationTable
            configurations={configurations || []}
            brandId={selectedBrand.id}
            brandName={selectedBrand.name}
            hasFiscalCalendar={hasFiscalCalendar}
            onRefresh={handleRefresh}
            refreshKey={refreshKey}
          />
        )}
      </div>

      {/* Add Configuration Sheet */}
      <Sheet open={isAddSheetOpen} onOpenChange={setIsAddSheetOpen}>
        <SheetContent 
          className="sm:max-w-2xl flex flex-col p-0"
          onInteractOutside={(e) => {
            // Prevent closing when interacting with Select/SingleSelect dropdowns
            const target = e.target as Element;
            if (
              target.closest('[role="listbox"]') || 
              target.closest('[data-radix-select-viewport]') ||
              target.closest('[class*="select-dropdown"]') ||
              target.closest('[class*="dropdown-menu"]')
            ) {
              e.preventDefault();
            }
          }}
        >
          <div className="px-6 pt-6">
            <SheetHeader>
              <SheetTitle>Add Report Configuration</SheetTitle>
              <SheetDescription>
                Configure a new data fetch for {selectedBrand?.name || 'the selected brand'}
              </SheetDescription>
            </SheetHeader>
          </div>
          {selectedBrand && (
            <ConfigurationForm
              brandId={selectedBrand.id}
              brandName={selectedBrand.name}
              hasFiscalCalendar={hasFiscalCalendar}
              existingConfigurations={configurations || []}
              onSuccess={handleAddSuccess}
              onCancel={() => setIsAddSheetOpen(false)}
            />
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default ReportConfiguration;
